/* Row / card internals reuse the real components' CSS (imported in the .tsx), so
   only the wrappers + a couple of fixed slots live here. */

/* The list-row key box. The real key is a CopyableId: the 4rem id text plus a
   spacing-1 gap and its 13px copy glyph (hidden at rest but holding its width) = 81px.
   Reserve the same so the title starts where the real title does (3rem key on phones). */
.keySlot {
	display: flex;
	flex: none;
	align-items: center;
	inline-size: 5.0625rem;
}

/* The ⋯ menu slot — invisible at rest in a real row, but it holds its 2rem box, so
   reserve it, keeping the type badge at the same offset from the row's trailing edge. */
.menuSlot {
	flex: none;
	inline-size: 2rem;
	block-size: 2rem;
}

@media (width <= 640px) {
	.keySlot {
		inline-size: 4.0625rem;
	}
}

/* Command-band header (BoardHeaderSkeleton) — the leaf bars whose width the reused
   header/command-bar CSS doesn't already fix. These are the measured real controls,
   with the phone step-down: the New action + Filters + Views drop to icon-only and
   the search grows to fill the row (matching board-search / filter-toolbar). */
.headerSearch {
	flex: 0 0 20rem;
}

.headerAction {
	inline-size: 8.75rem;
}

.headerFilters {
	inline-size: 7rem;
}

.headerViews {
	inline-size: 5.5rem;
}

/* Epics tier-2 controls: the owner filter ("Any owner") + the "Show archived" toggle,
   both wearing the same .control chrome (icon + spacing-3 padding + label) as Filters /
   Views but with their own labels, so wider. On desktop the controls are right-aligned
   with a flexible gap before them, so their width only shifts empty space; on phones
   they collapse to icon-only, the same 2.5rem square (spacing-3 padding + 16px icon). */
.headerOwner {
	inline-size: 7.5rem;
}

.headerArchive {
	inline-size: 9.25rem;
}

@media (width <= 640px) {
	.headerSearch {
		flex: 1 1 auto;
		min-inline-size: 0;
	}

	.headerAction {
		inline-size: 4rem;
	}

	.headerFilters {
		inline-size: 4rem;
	}

	.headerViews {
		inline-size: 2.625rem;
	}

	.headerOwner,
	.headerArchive {
		inline-size: 2.5rem;
	}
}

/* Phone column switcher row (BoardSwitcherSkeleton): shown only where the real
   StatusSwitcher is (phones); desktop/tablet show every column, so hide it there. */
.switcherRow {
	display: none;
}

@media (width <= 640px) {
	.switcherRow {
		display: block;
	}
}

/* Board lanes — mirrors board.component.module.css (minus the scroll fade). */
.board {
	display: flex;
	gap: var(--spacing-4);
	align-items: flex-start;
	padding-block: var(--spacing-2);
	overflow-x: hidden;

	/* Phone: single column, like the real mobile board (.mobileBoard) — which drops
	   the top padding (the switcher sits directly above), so match it or the column
	   slips 8px below where the real board lands. */
	@media (width <= 640px) {
		flex-direction: column;
		padding: 0 var(--spacing-1) var(--spacing-4);

		& > * {
			inline-size: 100%;
		}

		& > *:nth-child(n + 2) {
			display: none;
		}
	}
}

/* Task detail (drawer) — status-accent header + property chips + description. */
.detail {
	display: flex;
	flex-direction: column;
	block-size: 100%;
}

/* Matches the real title bar: the board's brand wash over the surface. */
.detailHeader {
	padding: var(--spacing-6) var(--spacing-6) var(--spacing-5);
	background:
		radial-gradient(
			120% 150% at 0% 0%,
			color-mix(in srgb, var(--colour-brand-primary) 10%, transparent),
			transparent 62%
		),
		var(--colour-background-surface);
	border-block-end: 1px solid var(--colour-border-subtle);
}

.detailBody {
	display: flex;
	flex-direction: column;
	gap: var(--spacing-3);
	padding: var(--spacing-5) var(--spacing-6);
}

.chips {
	display: flex;
	flex-wrap: wrap;
	gap: 0.45rem;
	margin-block-end: var(--spacing-2);
}

/* Boot shell — matches the measured sidebar (240 / pad 16×12 / gap 8) + main. */
.shell {
	display: flex;
	block-size: 100dvh;
	background-color: var(--colour-background-page);
}

.sidebar {
	display: flex;
	flex: none;
	flex-direction: column;
	gap: var(--spacing-2);
	inline-size: 240px;
	padding: var(--spacing-4) var(--spacing-3);
	background-color: var(--colour-background-surface);
	border-inline-end: 1px solid var(--colour-border-subtle);
}

.brandRow {
	display: flex;
	gap: var(--spacing-2);
	align-items: center;
	padding: var(--spacing-2) var(--spacing-2) var(--spacing-3);
}

.brandDot {
	display: block;
	inline-size: 12px;
	block-size: 12px;
	background: var(--colour-brand-primary);
	border-radius: var(--radius-sm);
}

.nav {
	display: flex;
	flex-direction: column;
	gap: var(--spacing-1);
	margin-block-start: var(--spacing-1);
}

.navItem {
	display: flex;
	gap: var(--spacing-3);
	align-items: center;
	min-block-size: 37px;
	padding: var(--spacing-2);
}

.spacer {
	flex: 1;
}

.account {
	display: flex;
	gap: var(--spacing-2);
	align-items: center;
	padding: var(--spacing-1);
}

.shellMain {
	flex: 1;
	min-inline-size: 0;
	padding: var(--spacing-5) var(--gutter);

	/* No overflow clip here: the real .main has none, and a scroll container would
	   capture the full-bleed band's sticky positioning and drop it ~24px (the padding)
	   below the -1 seam it sits at in every real view, so the whole boot shell would
	   jump down on hydration. The board lanes clip their own overflow-x. */
}

/* Phone: the sidebar is off-canvas, so the boot shows just the main content. */
@media (width <= 640px) {
	.sidebar {
		display: none;
	}

	.shellMain {
		padding: var(--spacing-4);
	}
}
